import React from 'react';
import { PuffLoader } from 'react-spinners';
import { Button, Input, InputTextArea, Label, SelectBox } from 'components';
import {
  registerPolarizationList,
  registerUplinkPCMCodeList,
  registerOperatingModeList
} from './utils';
import { FormData, formatNumber } from 'utils/validation';
import { useTranslation } from 'react-i18next';

interface Props {
  formData: FormData;
  onNext: (data: FormData) => void;
  errors: Partial<Record<keyof FormData, string>>;
  handleInputChange: (field: keyof FormData, value: any) => void;
  isLoading: boolean;
  error: string | null;
  handleFetchTLE: () => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const SUpLink: React.FC<Props> = ({
  formData,
  errors,
  handleInputChange,
  isLoading,
  error,
  handleFetchTLE,
  handleKeyDown
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      {/* 공통 필드 */}
      <Input
        label={t('manage.modal.name')}
        labelTooltip={t('manage.tooltip.name')}
        value={formData.name}
        onChange={(e) => handleInputChange('name', e.target.value)}
        errorMessage={errors.name}
      />
        {/* NORAD + 버튼 + 로더 : 상단 정렬로 고정 */}
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <Input
            label={t('manage.modal.noradId')}
            labelTooltip={t('manage.tooltip.noradId')}
            value={formData.noradId}
            onChange={(e) => handleInputChange('noradId', e.target.value)}
            onKeyDown={handleKeyDown}
            errorMessage={errors.noradId}
            placeholder={t('manage.placeholder.noradId')}
          />
        </div>

        {/* 버튼: 항상 상단 기준으로 고정 */}
        <Button
          className="self-start h-8 mt-6"
          onClick={handleFetchTLE}
          type="secondary"
          size="small"
        >
          {t('manage.button.getTle')}
        </Button>

        {/* 로더: 버튼과 같은 기준선 */}
        {isLoading && (
          <div className="self-start mt-6">
            <PuffLoader size={30} />
          </div>
        )}
      </div>
    {error && <div className="text-red-500">{error}</div>}
      <Input
        label={t('manage.modal.tleTitle')}
        value={formData.tleTitle}
        onChange={(e) => handleInputChange('tleTitle', e.target.value)}
        errorMessage={errors.tleTitle}
      />
      <Input
        label={t('manage.modal.tleLine1')}
        value={formData.tleFirst}
        onChange={(e) => handleInputChange('tleFirst', e.target.value)}
        errorMessage={errors.tleFirst}
      />
      <Input
        label={t('manage.modal.tleLine2')}
        value={formData.tleSecond}
        onChange={(e) => handleInputChange('tleSecond', e.target.value)}
        errorMessage={errors.tleSecond}
      />
      <Input
        label={t('manage.modal.bitRate')}
        labelTooltip="100 ~ 1,000,000 bps"
        placeholder="100 ~ 1,000,000 bps"
        value={formData.bitRate}
        onChange={(e) => handleInputChange('bitRate', formatNumber(e.target.value))}
        errorMessage={errors.bitRate}
      />
      <SelectBox
        className="max-w-none"
        bgColor="layer3"
        label={t('manage.modal.polarization')}
        list={registerPolarizationList}
        value={formData.polarization}
        onChange={(value) => handleInputChange('polarization', value)}
      />

      {/* Uplink 전용 필드 */}
      <SelectBox
        className="max-w-none"
        bgColor="layer3"
        label={t('manage.modal.pcmCoding')}
        list={registerUplinkPCMCodeList}
        value={formData.pcmCode || ''}
        onChange={(value) => handleInputChange('pcmCode', value)}
      />

      {/* Idle Pattern */}
      <div className="flex flex-col w-full">
        <div className="flex flex-row">
          <Label
            label="Idle Pattern"
            htmlFor="idlePattern"
            labelTooltip="Hexadecimal value 0xFF"
          />
        </div>
        <div className="flex flex-col w-full">
          <div className="flex flex-row justify-between items-center gap-1">
            <label htmlFor="idlePattern" className="text-text-secondary font-regular text-body2">0x</label>
            <input
              type="text"
              id="idlePattern"
              className="h-8 flex-1 rounded border bg-transparent px-3 text-body"
              placeholder="1ACFFC1D"
              value={formData.idlePattern || ''}
              onChange={(e) => handleInputChange('idlePattern', e.target.value)}
            />
          </div>
          {errors.idlePattern && (
            <p className="mt-2 text-red-500 text-body2">{errors.idlePattern}</p>
          )}
        </div>
      </div>

      <Input
        label="Idle Pattern Length"
        labelTooltip="0 ~ 32"
        placeholder="0 ~ 32"
        value={formData.idlePatternLength || ''}
        onChange={(e) => handleInputChange('idlePatternLength', formatNumber(e.target.value))}
        errorMessage={errors.idlePatternLength}
      />

      <Input
        label="Preamble Length"
        labelTooltip="0 ~ 16,777,214"
        placeholder="0 ~ 16,777,214"
        value={formData.preambleLength || ''}
        onChange={(e) => handleInputChange('preambleLength', formatNumber(e.target.value))}
        errorMessage={errors.preambleLength}
      />

      <Input
        label="Carrier Frequency (MHz)"
        labelTooltip="2025 ~ 2120"
        placeholder="2025 ~ 2120"
        value={formData.carrierFrequencyMhz || ''}
        onChange={(e) => handleInputChange('carrierFrequencyMhz', formatNumber(e.target.value))}
        errorMessage={errors.carrierFrequencyMhz}
      />

      <SelectBox
        className="max-w-none"
        bgColor="layer3"
        label="Operating Mode"
        list={registerOperatingModeList}
        value={formData.operatingMode || ''}
        onChange={(value) => handleInputChange('operatingMode', value)}
      />

      <Input
        label="IF Carrier Level (dB)"
        labelTooltip="Range: -80.0 to 10.0 (decimals allowed)"
        placeholder="-80.0 ~ 10.0"
        type="number"
        value={formData.ifCarrierLevel || ''}
        onChange={(e) => handleInputChange('ifCarrierLevel', e.target.value)}
        errorMessage={errors.ifCarrierLevel}
      />

      <Input
        label="Sweep Rate"
        labelTooltip="Range: 0.0001 to 175,000.0 (decimals allowed)"
        placeholder="0.0001 ~ 175,000.0"
        type="number"
        value={formData.sweepRate || ''}
        onChange={(e) => handleInputChange('sweepRate', e.target.value)}
        errorMessage={errors.sweepRate}
      />

      <Input
        label="Sweep Range"
        labelTooltip="1,000 ~ 1,000,000"
        placeholder="1,000 ~ 1,000,000"
        value={formData.sweepRange || ''}
        onChange={(e) => handleInputChange('sweepRange', formatNumber(e.target.value))}
        errorMessage={errors.sweepRange}
      />

      <InputTextArea
        label={t('manage.modal.other')}
        value={formData.other || ''}
        onChange={(value) => handleInputChange('other', value)}
      />
    </div>
  );
};

export default SUpLink;
